<script setup lang="ts">
import { type PropType, onMounted, ref, computed } from 'vue'
import { useStore } from '@nanostores/vue'
import { connectExpressCheckout, fetchCoupons } from '@/assets/ts/apiManagement'
import { type ICoupon } from '@/types/Coupon'
import CampaignCouponCard from '@/components/CampaignCouponCard.vue'
import CouponCenterDialogSheet from '@/components/CouponCenterDialogSheet.vue'
import Skeleton from '@/components/Skeleton.vue'
import { GaGeneralPageName } from '@/assets/ts/customEvent'
import { isLogin } from '@/store/userStore'
import type { SectionType } from './_MercariCampaign.vue'

interface Coupon {
	type: 'bnshop' | 'shipment' | 'b4u'
	code: string
}

interface Section {
	menuTitle: string
	title: string
	subtitle: string
	type: SectionType
	coupons?: Coupon[]
}

interface CampaignCoupon extends ICoupon {
	campaignType: 'bnshop' | 'shipment' | 'b4u'
}

const props = defineProps({
	section: {
		type: Object as PropType<Section>,
		required: true,
	},
	couponCardText: {
		type: Object,
		required: true,
	},
})

const isLoginGlobal = useStore(isLogin)
const allCoupons = ref<ICoupon[]>([])
const campaignCoupons = ref<CampaignCoupon[]>([])
const isOpen = ref(false)
const currentCoupon = ref<ICoupon | null>(null)
const isCouponLoading = ref(true)

const usage = computed(() => {
	if (currentCoupon.value?.order_type === 'bnshop') {
		return props.couponCardText.coupon_details_product_express
	}
	if (currentCoupon.value?.order_type === 'b4u') {
		return props.couponCardText.coupon_details_product_proxy
	}
	if (currentCoupon.value?.order_type === 'shipment') {
		return props.couponCardText.coupon_details_product_shipping
	}

	return props.couponCardText.coupon_details_product_express
})

const displayableCoupons = computed(() => {
	if (!props.section.coupons || isCouponLoading.value) return []

	return props.section.coupons
		.map(sectionCoupon => {
			const details = getCouponDetails(sectionCoupon.code)
			if (!details) return null

			return {
				code: sectionCoupon.code,
				details,
			}
		})
		.filter(item => item !== null)
})

const getCouponDetails = (code: string) => {
	const coupon = campaignCoupons.value.find(c => c.code === code)
	if (!coupon) return null

	return {
		type: 'mercari-campaign' as const,
		orderType: coupon.order_type,
		couponCode: coupon.code,
		couponAmount: coupon.coupon_amount ?? null,
		percentageDiscount: coupon.percentage_discount ?? null,
		title: coupon.title || '',
		startDate: coupon.start_date ?? null,
		expiryDate: coupon.expiry_date || '',
		isExpireSoon: coupon.is_expire_soon || false,
		terms: coupon.terms || '',
		discountType: coupon.discount_type,
		isDisabled: false,
		isInModal: false,
		currentPageName: 'Mercari Campaign Page',
	}
}

const getCouponByCode = (code: string): ICoupon | null => {
	return campaignCoupons.value.find(c => c.code === code) || null
}

const handleCouponCardClick = (code: string) => {
	const coupon = getCouponByCode(code)
	if (coupon) {
		showModal(coupon)
	}
}

const showModal = (coupon: ICoupon) => {
	currentCoupon.value = coupon
	isOpen.value = true
}

const updateIsOpen = (value: boolean) => {
	isOpen.value = value
}

const fetchCampaignCoupons = async () => {
	try {
		isCouponLoading.value = true

		const { coupon_list: couponList } = await fetchCoupons()
		allCoupons.value = couponList

		const sectionCoupons = props.section.coupons || []

		if (!sectionCoupons.length) {
			isCouponLoading.value = false
			return []
		}

		const validCoupons = sectionCoupons
			.map(campaignCoupon => {
				const couponDetails = allCoupons.value.find(
					coupon => coupon.code === campaignCoupon.code,
				)

				if (!couponDetails) return null

				return {
					...couponDetails,
					campaignType: campaignCoupon.type,
				} as CampaignCoupon
			})
			.filter((coupon): coupon is CampaignCoupon => coupon !== null)

		campaignCoupons.value = validCoupons

		isCouponLoading.value = false

		return validCoupons
	} catch (error) {
		console.error('Failed to fetch campaign coupons:', error)
		isCouponLoading.value = false
		return []
	}
}

onMounted(async () => {
	if (isLoginGlobal.value) {
		await connectExpressCheckout()
	}
	await fetchCampaignCoupons()
})
</script>

<template>
	<div class="flex flex-col gap-casual-friends">
		<!-- Common Title Area -->
		<div
			class="flex flex-col gap-strong-friends"
			v-if="displayableCoupons.length"
		>
			<h2 class="typography-h3-str">
				{{ section.title }}
			</h2>
			<h4 class="typography-s1-reg">
				{{ section.subtitle }}
			</h4>
		</div>

		<!-- Coupon List Area -->
		<div
			class="flex flex-wrap items-center justify-center gap-close-ish-friends"
		>
			<template v-if="isCouponLoading">
				<div v-for="i in 2" :key="`skeleton-${i}`" class="w-auto">
					<Skeleton
						class="h-[140px] w-[335px] mdp:w-[386px] [@media(min-width:375px)]:w-[350px]"
					/>
				</div>
			</template>

			<template v-else>
				<div
					v-for="coupon in displayableCoupons"
					:key="coupon.code"
					class="w-auto"
				>
					<CampaignCouponCard
						v-bind="coupon.details"
						:text="couponCardText"
						@show-modal="handleCouponCardClick(coupon.code)"
						:current-page-name="GaGeneralPageName.MercariCampaign"
					/>
				</div>
			</template>
		</div>

		<CouponCenterDialogSheet
			v-model:isOpen="isOpen"
			:coupon-card-data="currentCoupon"
			:coupon-card-text="couponCardText"
			:usage="usage"
			@update:is-open="updateIsOpen"
			:current-page-name="GaGeneralPageName.MercariCampaign"
		/>
	</div>
</template>
